import AdboardCard, { adBoardSelector } from "@/components/CarCard/AdboardCard";
import { $ } from "@/generated/zeus";
import { useCustomFetch } from "@/hooks/useCustomFetch";
import { useTypedLazyQuery, useTypedQuery } from "@/hooks/useZeus";
import { setPageTitle } from "@/lib/utils";
import Avatar from "@/shared/Avatar/Avatar";
import Loader from "@/shared/FullScreenLoader/Loading";
import { Player } from "@/types";
import _ from "lodash";
import moment from "moment";
import React, { FC, useEffect } from "react";
import { useParams } from "react-router-dom";
import AdboardMap from "../PageHome/AdboardMap";

export interface AuthorPageProps {
  className?: string;
}

const PublisherPage: FC<AuthorPageProps> = ({ className = "" }) => {
  const {
    workspaceKey
  } = useParams<{
    workspaceKey:string
  }>();

  const [selectedCity,setSelectedCity] = React.useState<string>("");
  
  const {data} = useTypedQuery({
    aggregateAdBoard:[{
      where:{
        workspace:{
          is:{
            key:{
              equals:workspaceKey
            }
          }
        }
      }
    },{
      _count:{
        workspaceId:true
      }
    }],
    workspace:[{
      where:{
        key:workspaceKey
      },
    },{
      createdAt:true,
      name:true,
      avatar:true, 
    }]
  })

  const [fetchAdboards,{data:adBoardsData,loading}] = useTypedLazyQuery({
    adBoards:[{
      where:{
        city:{
          equals:$('selectedCity','String')
        },
        workspace:{
          is:{
            key:{
              equals:workspaceKey
            }
          }
        },
        offlineBookingsEnabled:{
          equals:false
        },
        isLive:{
          equals:true
        }
      },
      take:20,
    },adBoardSelector]
  },{
    variables:{
      selectedCity:selectedCity
    }
  })

  const {data:playersData} = useCustomFetch<{
    players: Player[]
  }>('/players?workspaceId='+workspaceKey)

  const pois = React.useMemo(()=>{
    return playersData?.players.map(item=>{
      return {
        location:{
          lat:item.map?.lat||0,
          lng:item.map?.lng||0,
        },
        key:item.id
      }
    })||[]
  },[playersData])

  const cities:string[] = React.useMemo(()=>{
    return _.uniq(playersData?.players.map(item=>{
      return item.map.city
    }))||[]
  },[playersData])

  useEffect(()=>{

    if(!cities.length){
      return;
    }

    if(!selectedCity){
      setSelectedCity(cities[0])
      return;
    }

    fetchAdboards({
      variables:{
        selectedCity:selectedCity
      }
    })
  },[selectedCity,cities])

  useEffect(()=>{
    setPageTitle(`${workspaceKey} | AdBoard Booking`)
  },[])

  if(!data?.workspace){
    return <Loader/>
  }

  const renderSidebar = () => {
    return (
      <div className=" w-full flex flex-col items-center text-center sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-6 sm:space-y-7 px-0 sm:p-6 xl:p-8">
        <Avatar
          imgUrl={data.workspace?.avatar}
          hasChecked
          hasCheckedClass="w-6 h-6 -top-0.5 right-2"
          sizeClass="w-28 h-28"
        />

        {/* ---- */}
        <div className="space-y-3 text-center flex flex-col items-center">
          <h2 className="text-3xl font-semibold">{data?.workspace?.name}</h2>
          {/* <StartRating className="!text-base" /> */}
        </div>

        {/* ---- */}
        <div className="border-b border-neutral-200 dark:border-neutral-700 w-14"></div>

        {/* ---- */}
        <div className="space-y-4">
         
          <div className="flex items-center space-x-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
              />
            </svg>
            <span className="text-neutral-6000 dark:text-neutral-300">
              {data.aggregateAdBoard._count?.workspaceId} listings
            </span>
          </div> 

          <div className="flex items-center space-x-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-neutral-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-neutral-6000 dark:text-neutral-300">
              Joined in {moment(data.workspace?.createdAt).format('MMM-YYYY')}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderMap= ()=>{
    return <div className=" w-full sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700">
      <div className="aspect-w-5 aspect-h-5 sm:aspect-h-3">
        <div className="rounded-xl overflow-hidden">
          <AdboardMap pois={pois}/>
        </div>
      </div>
    </div>
  }

  const renderSection1 = () => {
    return (
      <div className="listingSection__wrap">
        <div>
          <h2 className="text-2xl font-semibold">{data?.workspace?.name}'s listings</h2>
          <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
            {data.workspace?.name}'s listings is very rich and diverse in many categories such as: Adboards, Billboards, etc.
          </span>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        <div>
          <div className="flex overflow-auto w-full gap-2 pb-2">
            {cities.map((item) => (
              <button
                onClick={()=>{
                  setSelectedCity(item)
                }}
                className={`flex-shrink-0 block !leading-none font-medium px-5 py-2.5 text-sm sm:text-base sm:px-6 sm:py-3 capitalize rounded-full focus:outline-none ${
                  selectedCity==item
                    ? "bg-secondary-900 text-secondary-50 "
                    : "text-neutral-500 dark:text-neutral-400 dark:hover:text-neutral-100 hover:text-neutral-900 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                } `}
              >
                {item}
              </button>
            ))}
          </div>  
          <div className="mt-8 grid grid-cols-1 gap-6 md:gap-7 sm:grid-cols-2 relative">
            {loading ? <Loader/> : adBoardsData?.adBoards.map((stay) => (
              <AdboardCard key={stay.id} data={stay} />
            ))}
          </div>
          
        </div>
      </div>
    );
  };

  return (
    <div className={`nc-AuthorPage ${className}`} data-nc-id="AuthorPage">
      <main className="container mt-12 mb-24 lg:mb-32 flex flex-col lg:flex-row">
        <div className="block flex-grow mb-24 lg:mb-0">
          <div className="lg:top-24 flex gap-4 flex-col">
            {renderSidebar()}

            {renderMap()}
          </div>
        </div>
        <div className="w-full lg:w-3/5 xl:w-2/3 space-y-8 lg:space-y-10 lg:pl-10 flex-shrink-0">
          {renderSection1()}
          {/* {renderSection2()} */}
        </div>
      </main>
    </div>
  );
};

export default React.memo(PublisherPage);
